import {
  keepPreviousData,
  useMutation,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  getAllKlaviyoLists,
  getCheckKlaviyoOptIn,
  getCompanyProfile,
  getComplianceReport,
  getComplianceReports,
  getConsentFormFields,
  getContactDetails,
  getContactHistory,
  getContactHistoryDetails,
  getContacts,
  getDatashipsKlaviyoLists,
  getKlaviyoHealthCheckHistory,
  getKlaviyoPrivateKey,
  getShopifyHealthCheckHistory,
  getShopifyIntegration,
  getShopifyStoreDetails,
  getSmsHealthChecks,
  getSmsOnboardingPolicyContent,
  getUsageDashboard,
  getWidgetCode,
  postCompanyProfile,
  postKlaviyoOptIn,
  postKlaviyoPrivateKey,
  postPrivacyEmail,
  postPrivacyPolicyUrl,
  postUpdateOnboardingProgress,
  postVerifyKlaviyoPrivateKey,
  setComplianceReport,
  updateKlaviyoList,
  updateKlaviyoListV2,
  postTogglePoweredByDatashipsPreference,
  getPoweredByDatashipsPreference,
  getEmailDashboard,
} from "@/requests/api/collect";

const useShopifyStoreDetails = () =>
  useQuery({
    queryKey: ["shopifyStoreDetails"],
    queryFn: getShopifyStoreDetails,
  });

const usePostPrivacyPolicyUrl = () =>
  useMutation({
    mutationFn: postPrivacyPolicyUrl,
  });

const usePostPrivacyEmail = () =>
  useMutation({
    mutationFn: postPrivacyEmail,
  });

const useConsentFormFields = (
  props?: Omit<UseQueryOptions<IGetConsentFieldsRS>, "queryKey"> & {
    queryKey?: string[];
  },
) =>
  useQuery({
    queryKey: ["collect", "consentFormFields"],
    queryFn: getConsentFormFields,
    ...props,
  });

const usePostKlaviyoOptIn = () =>
  useQuery({
    queryKey: ["postKlaviyoOptIn"],
    queryFn: postKlaviyoOptIn,
  });

const useGetCheckKlaviyoOptIn = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["klaviyoOptIn"],
    queryFn: getCheckKlaviyoOptIn,
    enabled,
    refetchInterval: query => {
      if (!query?.state?.data?.singleOptInListChecked) return 4000;
      return false;
    },
  });

const usePostUpdateOnboardingProgress = () =>
  useMutation({
    mutationFn: postUpdateOnboardingProgress,
  });

const usePostVerifyKlaviyoApiKey = ({
  privateKey,
  enabled,
}: {
  privateKey: string;
  enabled: boolean;
}) =>
  useQuery({
    enabled,
    queryKey: ["klaviyoOrganizationName", privateKey?.slice(-4)],
    queryFn: ({ signal }) =>
      postVerifyKlaviyoPrivateKey({ privateKey, signal }),
  });

const useGetKlaviyoApiKey = () =>
  useQuery({
    queryKey: ["klaviyoApiKey"],
    queryFn: getKlaviyoPrivateKey,
    staleTime: 0,
  });

const usePostKlaviyoPrivateKey = () =>
  useMutation({
    mutationFn: postKlaviyoPrivateKey,
  });

const useGetWidgetCode = () =>
  useQuery({
    queryKey: ["widgetCode"],
    queryFn: getWidgetCode,
  });

const useGetShopifyIntegration = () =>
  useQuery({
    queryKey: ["shopifyIntegration"],
    queryFn: getShopifyIntegration,
  });

const useGetEmailDashboard = (payload: IGetEmailDashboardRQ) =>
  useQuery({
    queryKey: ["dashboardData", payload],
    queryFn: ({ signal }) => getEmailDashboard({ payload, signal }),
  });

const useGetUsageDashboard = (payload: IGetDashboardRQ) =>
  useQuery({
    queryKey: ["usageDashboardData", payload],
    queryFn: ({ signal }) => getUsageDashboard({ payload, signal }),
  });

const useGetComplianceReport = (reportId?: string) =>
  useQuery({
    queryKey: ["complianceReport", reportId],
    queryFn: ({ signal }) => getComplianceReport({ reportId, signal }),
  });

const useSetComplianceReport = () =>
  useMutation({
    mutationFn: setComplianceReport,
  });

const useGetComplianceReportsTable = ({
  enabled,
  queryKey,
  params,
}: ITableOptions) =>
  useQuery({
    enabled,
    queryKey,
    queryFn: ({ signal }) => getComplianceReports({ params, signal }),
    placeholderData: keepPreviousData,
  });

const useGetContactsTable = ({ enabled, queryKey, params }: ITableOptions) =>
  useQuery({
    enabled,
    queryKey,
    queryFn: ({ signal }) => getContacts({ params, signal }),
    placeholderData: keepPreviousData,
  });

const useGetContactDetails = (contactId?: string) =>
  useQuery({
    queryKey: ["contactDetails", contactId],
    queryFn: () => getContactDetails(contactId),
  });

const useGetContactHistoryTable = ({
  enabled,
  queryKey,
  params,
}: ITableOptions) =>
  useQuery({
    enabled,
    queryKey,
    queryFn: () => getContactHistory(params),
    placeholderData: keepPreviousData,
  });

const useGetContactHistoryDetails = ({
  enabled,
  contactHistoryId,
}: {
  enabled: boolean;
  contactHistoryId?: string;
}) =>
  useQuery({
    queryKey: ["contactHistoryDetails", contactHistoryId],
    queryFn: () => getContactHistoryDetails(contactHistoryId),
    enabled,
  });

const useGetSmsHealthChecks = () =>
  useQuery({
    queryKey: ["healthChecks"],
    queryFn: getSmsHealthChecks,
  });

const useGetShopifyHealthCheckHistoryTable = ({
  enabled,
  queryKey,
  params,
}: ITableOptions) =>
  useQuery({
    enabled,
    queryKey,
    queryFn: ({ signal }) => getShopifyHealthCheckHistory({ params, signal }),
    placeholderData: keepPreviousData,
  });

const useGetKlaviyoHealthCheckHistoryTable = ({
  enabled,
  queryKey,
  params,
}: ITableOptions) =>
  useQuery({
    enabled,
    queryKey,
    queryFn: ({ signal }) => getKlaviyoHealthCheckHistory({ params, signal }),
    placeholderData: keepPreviousData,
  });

const useGetSmsOnboardingPolicyContent = (token: string) =>
  useQuery({
    queryKey: ["smsOnboardingPolicyContent"],
    queryFn: ({ signal }) => getSmsOnboardingPolicyContent({ signal, token }),
  });

const useGetDatashipsKlaviyoLists = (isKlaviyoV2EndpointEnabled: boolean) =>
  useQuery({
    queryKey: ["datashipsKlaviyoLists"],
    queryFn: ({ signal }) =>
      getDatashipsKlaviyoLists({ signal, isKlaviyoV2EndpointEnabled }),
  });

const useGetAllKlaviyoLists = () =>
  useQuery({
    queryKey: ["allKlaviyoLists"],
    queryFn: getAllKlaviyoLists,
  });

const usePostUpdateKlaviyoList = (isKlaviyoV2EndpointEnabled: boolean) =>
  useMutation({
    mutationFn: isKlaviyoV2EndpointEnabled
      ? updateKlaviyoListV2
      : updateKlaviyoList,
  });

const useGetCompanyProfile = () =>
  useQuery({
    queryKey: ["customer", "profile"],
    queryFn: getCompanyProfile,
  });

const usePostCompanyProfile = () =>
  useMutation({
    mutationFn: postCompanyProfile,
  });

const useGetPoweredByDatashipsPreference = () =>
  useQuery({
    queryKey: ["poweredByDatashipsPreference"],
    queryFn: getPoweredByDatashipsPreference,
  });

const usePostTogglePoweredByDatashipsPreference = () =>
  useMutation({
    mutationFn: postTogglePoweredByDatashipsPreference,
  });

export {
  useShopifyStoreDetails,
  usePostKlaviyoOptIn,
  useGetCheckKlaviyoOptIn,
  usePostUpdateOnboardingProgress,
  usePostKlaviyoPrivateKey,
  usePostVerifyKlaviyoApiKey,
  useGetKlaviyoApiKey,
  usePostPrivacyPolicyUrl,
  usePostPrivacyEmail,
  useConsentFormFields,
  useGetWidgetCode,
  useGetShopifyIntegration,
  useGetEmailDashboard,
  useGetUsageDashboard,
  useGetComplianceReport,
  useSetComplianceReport,
  useGetComplianceReportsTable,
  useGetContactsTable,
  useGetContactDetails,
  useGetContactHistoryTable,
  useGetContactHistoryDetails,
  useGetSmsHealthChecks,
  useGetShopifyHealthCheckHistoryTable,
  useGetKlaviyoHealthCheckHistoryTable,
  useGetSmsOnboardingPolicyContent,
  useGetDatashipsKlaviyoLists,
  useGetAllKlaviyoLists,
  usePostUpdateKlaviyoList,
  useGetCompanyProfile,
  usePostCompanyProfile,
  useGetPoweredByDatashipsPreference,
  usePostTogglePoweredByDatashipsPreference,
};
